/**
 * TrlContext
 *
 * Trailblazers-game-specific state owned outside GameContext:
 *   - team-management helpers that emit the TRL_* socket events with ACK
 *     callbacks (team create/rename/delete, player assign/remove, team-name
 *     config).
 *
 * Mounted by GameTypeProvider only when the selected game is a Trailblazers
 * game. Team/game state itself (game.trlTeams, game.trlTeamNameConfig) arrives
 * via GAME_STATE_UPDATED on the universal Game object and is read from
 * GameContext — this context only owns the emit helpers. The reusable
 * screenshot library lives in the Content Library (TrlImageLibraryProvider,
 * mounted app-level via ContentLibraryProviders).
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { useGameContext } from './GameContext';
import {
  SocketEvent,
  TrlCreateTeamRequest,
  TrlCreateTeamResponse,
  TrlUpdateTeamRequest,
  TrlUpdateTeamResponse,
  TrlDeleteTeamRequest,
  TrlDeleteTeamResponse,
  TrlAssignPlayerRequest,
  TrlAssignPlayerResponse,
  TrlRemovePlayerRequest,
  TrlRemovePlayerResponse,
  TrlSetTeamCaptainRequest,
  TrlSetTeamCaptainResponse,
  TrlUpdateTeamNameConfigRequest,
  TrlUpdateTeamNameConfigResponse,
  TrlUpdateStalemateConfigRequest,
  TrlUpdateStalemateConfigResponse,
  TrlUpdateRadarConfigRequest,
  TrlUpdateRadarConfigResponse,
  TrlUpdateTreasureConfigRequest,
  TrlUpdateTreasureConfigResponse,
  TrlUpdateTriangulationConfigRequest,
  TrlUpdateTriangulationConfigResponse,
  TrlUpdateDisruptionConfigRequest,
  TrlUpdateDisruptionConfigResponse,
  TrlAdminClearDisruptionRequest,
  TrlAdminClearDisruptionResponse,
  TrlUpdateSabotageConfigRequest,
  TrlUpdateSabotageConfigResponse,
  TrlSaveMapRequest,
  TrlSaveMapResponse,
  TrlLoadMapRequest,
  TrlLoadMapResponse,
  TrlReleaseCheckpointRequest,
  TrlReleaseCheckpointResponse,
  TrlResyncDiscordRequest,
  TrlResyncDiscordResponse,
  TrlAdminForceClearNodeRequest,
  TrlAdminForceClearNodeResponse,
  TrlAdminSetCurrentNodeRequest,
  TrlAdminSetCurrentNodeResponse,
  TrlAdminMarkTeamFinishedRequest,
  TrlAdminMarkTeamFinishedResponse,
  TrlAdminRevealClueRequest,
  TrlAdminRevealClueResponse,
  TrlAdminResendSnapshotRequest,
  TrlAdminResendSnapshotResponse,
  AdminGetTrlResultsRequest,
  AdminTrlResultsResponse,
} from '@swtorbit/shared-socket-events';

interface TrlContextType {
  // ── Team management (socket ACK) ───────────────────────────────────────
  createTeam: (request: TrlCreateTeamRequest, callback?: (resp: TrlCreateTeamResponse) => void) => void;
  /** Rename and/or recolour a team (also updates its Discord channel/role). */
  updateTeam: (request: TrlUpdateTeamRequest, callback?: (resp: TrlUpdateTeamResponse) => void) => void;
  deleteTeam: (request: TrlDeleteTeamRequest, callback?: (resp: TrlDeleteTeamResponse) => void) => void;
  assignPlayer: (request: TrlAssignPlayerRequest, callback?: (resp: TrlAssignPlayerResponse) => void) => void;
  removePlayer: (request: TrlRemovePlayerRequest, callback?: (resp: TrlRemovePlayerResponse) => void) => void;
  setTeamCaptain: (request: TrlSetTeamCaptainRequest, callback?: (resp: TrlSetTeamCaptainResponse) => void) => void;
  updateTeamNameConfig: (
    request: TrlUpdateTeamNameConfigRequest,
    callback?: (resp: TrlUpdateTeamNameConfigResponse) => void
  ) => void;
  /** Retry Discord team channel/role creation (recovery when the bot was down at Start Game). */
  resyncDiscord: (
    request: TrlResyncDiscordRequest,
    callback?: (resp: TrlResyncDiscordResponse) => void
  ) => void;
  updateStalemateConfig: (
    request: TrlUpdateStalemateConfigRequest,
    callback?: (resp: TrlUpdateStalemateConfigResponse) => void
  ) => void;
  updateRadarConfig: (
    request: TrlUpdateRadarConfigRequest,
    callback?: (resp: TrlUpdateRadarConfigResponse) => void
  ) => void;
  updateTreasureConfig: (
    request: TrlUpdateTreasureConfigRequest,
    callback?: (resp: TrlUpdateTreasureConfigResponse) => void
  ) => void;
  updateTriangulationConfig: (
    request: TrlUpdateTriangulationConfigRequest,
    callback?: (resp: TrlUpdateTriangulationConfigResponse) => void
  ) => void;
  updateDisruptionConfig: (
    request: TrlUpdateDisruptionConfigRequest,
    callback?: (resp: TrlUpdateDisruptionConfigResponse) => void
  ) => void;
  /** Which sabotage types the game allows (locked once the game leaves setup). */
  updateSabotageConfig: (
    request: TrlUpdateSabotageConfigRequest,
    callback?: (resp: TrlUpdateSabotageConfigResponse) => void
  ) => void;

  // ── Galaxy-map authoring (socket ACK) ──────────────────────────────────
  /** Persist the full authored map; the server reconciles ids + validates. */
  saveMap: (request: TrlSaveMapRequest, callback?: (resp: TrlSaveMapResponse) => void) => void;
  /** Fetch the current map for a game (creates an empty draft if none). */
  loadMap: (request: TrlLoadMapRequest, callback?: (resp: TrlLoadMapResponse) => void) => void;

  // ── Live game control (socket ACK) ─────────────────────────────────────
  /** Release every team held at a checkpoint without waiting for the field. */
  releaseCheckpoint: (
    request: TrlReleaseCheckpointRequest,
    callback?: (resp: TrlReleaseCheckpointResponse) => void
  ) => void;
  /** Clear a player's active disruption block (their scanner works again). */
  clearDisruption: (
    request: TrlAdminClearDisruptionRequest,
    callback?: (resp: TrlAdminClearDisruptionResponse) => void
  ) => void;

  // ── Admin live-game overrides (socket ACK) ─────────────────────────────
  /**
   * Clear a planet for a team that cannot clear it itself. Scores the natural
   * rank but never awards the node's treasure.
   */
  forceClearNode: (
    request: TrlAdminForceClearNodeRequest,
    callback?: (resp: TrlAdminForceClearNodeResponse) => void
  ) => void;
  /** Place a team on a planet — recovers a lost fork pick or a mis-clear. */
  setCurrentNode: (
    request: TrlAdminSetCurrentNodeRequest,
    callback?: (resp: TrlAdminSetCurrentNodeResponse) => void
  ) => void;
  /** Finish a team outright so it stops holding the field at a checkpoint. */
  markTeamFinished: (
    request: TrlAdminMarkTeamFinishedRequest,
    callback?: (resp: TrlAdminMarkTeamFinishedResponse) => void
  ) => void;
  /** Unlock one of a team's stalemate clues now, without waiting for its timer. */
  revealClue: (
    request: TrlAdminRevealClueRequest,
    callback?: (resp: TrlAdminRevealClueResponse) => void
  ) => void;
  /** Re-push the masked snapshot — heals a client stuck on stale state. */
  resendSnapshot: (
    request: TrlAdminResendSnapshotRequest,
    callback?: (resp: TrlAdminResendSnapshotResponse) => void
  ) => void;

  // ── Admin statistics snapshot ──────────────────────────────────────────
  /**
   * The last stats snapshot the server sent, or null before the first arrives.
   *
   * Server-built rather than derived from `selectedGame`, because the live Game
   * object carries a team's route but no capture times, ranks, points or
   * per-planet clocks — see AdminTrlResultsService.
   */
  trlAdminResults: AdminTrlResultsResponse | null;
  /** Ask the server for a fresh snapshot. */
  refreshTrlAdminResults: () => void;
}

const TrlContext = createContext<TrlContextType | undefined>(undefined);

interface TrlProviderProps {
  children: ReactNode;
}

export function TrlProvider({ children }: TrlProviderProps) {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();

  const [trlAdminResults, setTrlAdminResults] = useState<AdminTrlResultsResponse | null>(null);

  /*
   * Read through a ref so `refreshTrlAdminResults` keeps a stable identity.
   * Components call it from an effect keyed on the room code; if the callback
   * were rebuilt on every game update that effect would re-fire on every socket
   * broadcast and re-request the whole snapshot.
   */
  const selectedGameRef = useRef(selectedGame);
  selectedGameRef.current = selectedGame;

  const refreshTrlAdminResults = useCallback(() => {
    const game = selectedGameRef.current;
    if (!game || !socket) return;
    const req: AdminGetTrlResultsRequest = { gameId: game.dbId };
    socket.emit(SocketEvent.TRL_ADMIN_GET_RESULTS, req);
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    const onResults = (payload: AdminTrlResultsResponse) => {
      // A reply for a game no longer selected must not overwrite the current one.
      if (payload.gameId !== selectedGameRef.current?.dbId) return;
      setTrlAdminResults(payload);
    };
    socket.on(SocketEvent.TRL_ADMIN_RESULTS, onResults);
    return () => {
      socket.off(SocketEvent.TRL_ADMIN_RESULTS, onResults);
    };
  }, [socket]);

  /*
   * Drop a previous game's snapshot the moment the selection changes, so the
   * Results tab cannot show one game's statistics under another's name during
   * the round-trip for the new one.
   */
  useEffect(() => {
    setTrlAdminResults(null);
  }, [selectedGame?.dbId]);

  const createTeam = useCallback(
    (request: TrlCreateTeamRequest, callback?: (resp: TrlCreateTeamResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_CREATE_TEAM, request, (resp: TrlCreateTeamResponse) => callback?.(resp));
    },
    [socket]
  );

  const updateTeam = useCallback(
    (request: TrlUpdateTeamRequest, callback?: (resp: TrlUpdateTeamResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_UPDATE_TEAM, request, (resp: TrlUpdateTeamResponse) => callback?.(resp));
    },
    [socket]
  );

  const deleteTeam = useCallback(
    (request: TrlDeleteTeamRequest, callback?: (resp: TrlDeleteTeamResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_DELETE_TEAM, request, (resp: TrlDeleteTeamResponse) => callback?.(resp));
    },
    [socket]
  );

  const assignPlayer = useCallback(
    (request: TrlAssignPlayerRequest, callback?: (resp: TrlAssignPlayerResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_ASSIGN_PLAYER, request, (resp: TrlAssignPlayerResponse) => callback?.(resp));
    },
    [socket]
  );

  const removePlayer = useCallback(
    (request: TrlRemovePlayerRequest, callback?: (resp: TrlRemovePlayerResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_REMOVE_PLAYER, request, (resp: TrlRemovePlayerResponse) => callback?.(resp));
    },
    [socket]
  );

  const setTeamCaptain = useCallback(
    (request: TrlSetTeamCaptainRequest, callback?: (resp: TrlSetTeamCaptainResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_SET_TEAM_CAPTAIN, request, (resp: TrlSetTeamCaptainResponse) => callback?.(resp));
    },
    [socket]
  );

  const updateTeamNameConfig = useCallback(
    (request: TrlUpdateTeamNameConfigRequest, callback?: (resp: TrlUpdateTeamNameConfigResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_TEAM_NAME_CONFIG,
        request,
        (resp: TrlUpdateTeamNameConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const resyncDiscord = useCallback(
    (request: TrlResyncDiscordRequest, callback?: (resp: TrlResyncDiscordResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_RESYNC_DISCORD, request, (resp: TrlResyncDiscordResponse) => callback?.(resp));
    },
    [socket]
  );

  const updateStalemateConfig = useCallback(
    (request: TrlUpdateStalemateConfigRequest, callback?: (resp: TrlUpdateStalemateConfigResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_STALEMATE_CONFIG,
        request,
        (resp: TrlUpdateStalemateConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const updateRadarConfig = useCallback(
    (request: TrlUpdateRadarConfigRequest, callback?: (resp: TrlUpdateRadarConfigResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_RADAR_CONFIG,
        request,
        (resp: TrlUpdateRadarConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const updateTreasureConfig = useCallback(
    (request: TrlUpdateTreasureConfigRequest, callback?: (resp: TrlUpdateTreasureConfigResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_TREASURE_CONFIG,
        request,
        (resp: TrlUpdateTreasureConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const updateTriangulationConfig = useCallback(
    (
      request: TrlUpdateTriangulationConfigRequest,
      callback?: (resp: TrlUpdateTriangulationConfigResponse) => void
    ) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_TRIANGULATION_CONFIG,
        request,
        (resp: TrlUpdateTriangulationConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const updateDisruptionConfig = useCallback(
    (request: TrlUpdateDisruptionConfigRequest, callback?: (resp: TrlUpdateDisruptionConfigResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_DISRUPTION_CONFIG,
        request,
        (resp: TrlUpdateDisruptionConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const updateSabotageConfig = useCallback(
    (request: TrlUpdateSabotageConfigRequest, callback?: (resp: TrlUpdateSabotageConfigResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_UPDATE_SABOTAGE_CONFIG,
        request,
        (resp: TrlUpdateSabotageConfigResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const clearDisruption = useCallback(
    (request: TrlAdminClearDisruptionRequest, callback?: (resp: TrlAdminClearDisruptionResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_ADMIN_CLEAR_DISRUPTION,
        request,
        (resp: TrlAdminClearDisruptionResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const saveMap = useCallback(
    (request: TrlSaveMapRequest, callback?: (resp: TrlSaveMapResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_SAVE_MAP, request, (resp: TrlSaveMapResponse) => callback?.(resp));
    },
    [socket]
  );

  const loadMap = useCallback(
    (request: TrlLoadMapRequest, callback?: (resp: TrlLoadMapResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.TRL_LOAD_MAP, request, (resp: TrlLoadMapResponse) => callback?.(resp));
    },
    [socket]
  );

  const releaseCheckpoint = useCallback(
    (request: TrlReleaseCheckpointRequest, callback?: (resp: TrlReleaseCheckpointResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_RELEASE_CHECKPOINT,
        request,
        (resp: TrlReleaseCheckpointResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const forceClearNode = useCallback(
    (request: TrlAdminForceClearNodeRequest, callback?: (resp: TrlAdminForceClearNodeResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_ADMIN_FORCE_CLEAR_NODE,
        request,
        (resp: TrlAdminForceClearNodeResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const setCurrentNode = useCallback(
    (request: TrlAdminSetCurrentNodeRequest, callback?: (resp: TrlAdminSetCurrentNodeResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_ADMIN_SET_CURRENT_NODE,
        request,
        (resp: TrlAdminSetCurrentNodeResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const markTeamFinished = useCallback(
    (request: TrlAdminMarkTeamFinishedRequest, callback?: (resp: TrlAdminMarkTeamFinishedResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_ADMIN_MARK_TEAM_FINISHED,
        request,
        (resp: TrlAdminMarkTeamFinishedResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const revealClue = useCallback(
    (request: TrlAdminRevealClueRequest, callback?: (resp: TrlAdminRevealClueResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_ADMIN_REVEAL_CLUE,
        request,
        (resp: TrlAdminRevealClueResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const resendSnapshot = useCallback(
    (request: TrlAdminResendSnapshotRequest, callback?: (resp: TrlAdminResendSnapshotResponse) => void) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(
        SocketEvent.TRL_ADMIN_RESEND_SNAPSHOT,
        request,
        (resp: TrlAdminResendSnapshotResponse) => callback?.(resp)
      );
    },
    [socket]
  );

  const value = useMemo(
    () => ({
      createTeam,
      updateTeam,
      deleteTeam,
      assignPlayer,
      removePlayer,
      setTeamCaptain,
      updateTeamNameConfig,
      resyncDiscord,
      updateStalemateConfig,
      updateRadarConfig,
      updateTreasureConfig,
      updateTriangulationConfig,
      updateDisruptionConfig,
      updateSabotageConfig,
      saveMap,
      loadMap,
      releaseCheckpoint,
      clearDisruption,
      forceClearNode,
      setCurrentNode,
      markTeamFinished,
      revealClue,
      resendSnapshot,
      trlAdminResults,
      refreshTrlAdminResults,
    }),
    [
      createTeam,
      updateTeam,
      deleteTeam,
      assignPlayer,
      removePlayer,
      setTeamCaptain,
      updateTeamNameConfig,
      resyncDiscord,
      updateStalemateConfig,
      updateRadarConfig,
      updateTreasureConfig,
      updateTriangulationConfig,
      updateDisruptionConfig,
      updateSabotageConfig,
      saveMap,
      loadMap,
      releaseCheckpoint,
      clearDisruption,
      forceClearNode,
      setCurrentNode,
      markTeamFinished,
      revealClue,
      resendSnapshot,
      trlAdminResults,
      refreshTrlAdminResults,
    ]
  );

  return <TrlContext.Provider value={value}>{children}</TrlContext.Provider>;
}

export function useTrlContext() {
  const context = useContext(TrlContext);
  if (context === undefined) {
    throw new Error('useTrlContext must be used within a TrlProvider');
  }
  return context;
}
